import { Component, ViewEncapsulation, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '../../service/auth.service';
import { ActivatedRoute } from '@angular/router';
import { ActionState } from '../../model/enums/auth-state.enum';
import { AuthProcess } from '../../model/enums/auth.process';
import { fadeInGlideDown, fadeInGlideUp } from '@shared/utils/animations';
import { LocalStorageKeys } from '../../model/enums/local-storage.keys';
import { AuthMessageResponse } from '../../model/interfaces/auth-message-response.interface';
import { toCamelCase } from '@shared/utils/string-case-manipulation';
import { ENVIRONMENT } from '../../../../environments/environment';
import { User } from '../../model/interfaces/user.interface';
import { LocalStorageService } from '@shared/service/local-storage/local-storage.service';
/* eslint-disable @angular-eslint/prefer-standalone */
@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  animations: [fadeInGlideDown(), fadeInGlideUp()],
  standalone: false,
  encapsulation: ViewEncapsulation.None,
})
export class LoginComponent {
  protected authService = inject(AuthService);
  private route = inject(ActivatedRoute);
  private localStorageService = inject(LocalStorageService);

  readonly icons = {
    chevronLeft: {
      name: 'fa-chevron-left',
      size: 'fa-xs',
    },
  };

  state: ActionState = ActionState.PENDING;
  forgotPasswordLinkVisible = false;
  form!: FormGroup;
  messages: AuthMessageResponse[] = [];
  projectName!: string;
  blockTimeLeft?: string;
  checkingAuth = true; // Show loading until auth checks complete
  private redirectUrl?: string;

  constructor() {
    this.route.queryParamMap.subscribe((queryParams) => {
      let nextUrl = queryParams.get('next') || undefined;

      // CRITICAL: If no 'next' param in URL, check localStorage for SSO redirect URL
      // This handles the case where the backend doesn't preserve RelayState after SAML auth
      if (!nextUrl) {
        const storedSsoRedirectUrl = this.authService.getAndClearSsoRedirectUrl();
        if (storedSsoRedirectUrl) {
          nextUrl = storedSsoRedirectUrl;
        }
      }

      // CRITICAL: Sanitize redirect URL to prevent invalid redirects
      // Don't redirect to auth pages (login, logout, mfa, etc.) or invalid URLs
      if (nextUrl && !this.isValidRedirectUrl(nextUrl)) {
        this.redirectUrl = undefined;
      } else {
        // Decode URL if it's still encoded (handles double-encoding from email links)
        if (nextUrl && nextUrl.includes('%')) {
          try {
            this.redirectUrl = decodeURIComponent(nextUrl);
          } catch {
            this.redirectUrl = nextUrl;
          }
        } else {
          this.redirectUrl = nextUrl;
        }
      }
    });

    this.route.paramMap.subscribe((params) => {
      this.authService.projectName = params.get('project') || '';
      this.projectName = params.get('project') || '';

      // First check if already logged in
      this.authService
        .isLoggedIn(this.authService.projectName)
        .then((response) => {
          if (response.data) {
            this.authService.user = toCamelCase(response.data) as User;
            this.authService.navigateTo(this.redirectUrl || ENVIRONMENT.DEFAULT_VIEW.route);
            // Keep checkingAuth=true to prevent form flash before redirect
          } else {
            // User is not logged in, show form
            this.checkingAuth = false;
          }
        })
        .catch(() => {
          // Not logged in - clear saved language preference so it falls back to browser default
          this.localStorageService.removeItem('selectedLanguage');

          // Check project auth type for SSO redirect
          if (this.projectName) {
            const nextUrl = this.redirectUrl || ENVIRONMENT.DEFAULT_VIEW.route;
            this.authService.checkProject(this.projectName, nextUrl).subscribe({
              next: (data) => {
                // If local auth, show login form
                // If SAML, checkProject already triggered window.location.href redirect (keep loading)
                if (data?.authentication === 'local') {
                  this.checkingAuth = false;
                }
                // For SAML2, keep checkingAuth=true to prevent form flash before redirect
              },
              error: () => {
                // On error, show login form
                this.checkingAuth = false;
              },
            });
          } else {
            // No project name, show login form
            this.checkingAuth = false;
          }
        });
    });

    this.form = new FormGroup({
      username: new FormControl('', [Validators.required, Validators.minLength(3)]),
      password: new FormControl('', [Validators.required, Validators.minLength(3)]),
    });
  }

  private isValidRedirectUrl(url: string): boolean {
    // Remove leading/trailing whitespace
    url = url.trim();

    // Invalid patterns that should not be used as redirect URLs
    const invalidPatterns = [
      '/login',
      '/logout',
      '/mfa',
      '/reset-password',
      '/forgot-password',
      '/change-password',
      '/timeout',
      '/app/login',
      '/app/logout',
      '/app/mfa',
    ];

    // Check if URL starts with any invalid pattern
    for (const pattern of invalidPatterns) {
      if (url.startsWith(pattern) || url.includes(pattern)) {
        return false;
      }
    }

    // URL must start with / and not be just /
    return url.startsWith('/') && url.length > 1;
  }

  showForgetPassword(): void {
    // Store username so forgot-password page can pre-fill the email field
    const username = this.form.controls['username'].value || '';
    if (username) {
      this.authService.username = username;
    }
    this.authService.navigateTo('/forgot-password');
  }

  onSubmit(): void {
    if (this.form.valid) {
      this.authService.handleFormAction(this);

      const username: string = this.form.controls['username'].value || '';
      const password: string = this.form.controls['password'].value || '';

      this.authService.login(username, this.form.controls['password'].value || '').subscribe({
        next: (response) => {
          const mfaType = response?.data?.auth;
          if (mfaType && [AuthProcess.MFA_PROCESS, AuthProcess.MFA_ENFORCE].indexOf(mfaType) !== -1) {
            this.authService.username = username;
            this.authService.password = password;
            this.authService.mfaType = mfaType;

            // Build MFA URL and include 'next' parameter if it exists
            let mfaUrl = `/mfa${this.authService.projectName ? '/' + this.authService.projectName : ''}`;
            if (this.redirectUrl) {
              mfaUrl += `?next=${encodeURIComponent(this.redirectUrl)}`;
            }
            this.authService.navigateTo(mfaUrl);
            return;
          }

          if (response.data) {
            this.authService.user = toCamelCase(response.data) as User;
          }
          this.authService.setState(this, ActionState.SUCCESS);
          localStorage.setItem(LocalStorageKeys.LOGIN_PROJECT_NAME, this.projectName);
          this.authService.navigateTo(this.redirectUrl || ENVIRONMENT.DEFAULT_VIEW.route);
        },
        error: (response) => {
          let code: string = response?.code || '';

          if (response.time_left) {
            code = `${code}_TEMPORARY`;
            this.authService.blockTimeLeft = response.time_left;
          }

          if (code === AuthProcess.PASSWORD_RENEW || code === AuthProcess.AUTH_PASSWORD_RENEW) {
            this.authService.localStorageService.setItem(LocalStorageKeys.USER_NAME, username);
            this.authService.localStorageService.setItem(LocalStorageKeys.CURRENT_PASSWORD, password);
            this.authService.navigateTo('/change-password');
            return;
          }

          this.authService.finalizeAction(this, ActionState.FAILURE, [code]);
          this.forgotPasswordLinkVisible = true;
        },
      });
    }
  }
}
